import { useEffect, useRef, useState } from "react";
import api, { formatError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Camera, CheckCircle2, RefreshCw, Loader2 } from "lucide-react";
import { toast } from "sonner";

async function compressImage(file, maxSide = 1600, quality = 0.82) {
  const bitmap = await createImageBitmap(file).catch(() => null);
  if (!bitmap) return file;
  const scale = Math.min(1, maxSide / Math.max(bitmap.width, bitmap.height));
  const canvas = document.createElement("canvas");
  canvas.width = Math.round(bitmap.width * scale);
  canvas.height = Math.round(bitmap.height * scale);
  canvas.getContext("2d").drawImage(bitmap, 0, 0, canvas.width, canvas.height);
  const blob = await new Promise((res) => canvas.toBlob(res, "image/jpeg", quality));
  return blob || file;
}

export function useClosingPhoto(shiftId, side) {
  const [url, setUrl] = useState(null);
  useEffect(() => {
    if (!shiftId) { setUrl(null); return; }
    let active = true; let obj;
    api.get(`/shifts/${shiftId}/closing-photo`, { params: { side }, responseType: "blob" })
      .then((r) => { if (active) { obj = URL.createObjectURL(r.data); setUrl(obj); } })
      .catch(() => {});
    return () => { active = false; if (obj) URL.revokeObjectURL(obj); };
  }, [shiftId, side]);
  return url;
}

export default function ClosingPhotoCapture({ shiftId, side = "1", label, existing, onUploaded }) {
  const inputRef = useRef(null);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState(0);
  const saved = useClosingPhoto(existing ? shiftId : null, side);
  const shown = preview || (existing && saved);

  const pick = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true); setProgress(0);
    try {
      const blob = await compressImage(file);
      const fd = new FormData();
      fd.append("file", blob, "closing-slip.jpg");
      const { data } = await api.post(`/shifts/${shiftId}/closing-photo`, fd, {
        params: { side },
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (p) => setProgress(p.total ? Math.round((p.loaded / p.total) * 100) : 50),
      });
      setPreview(URL.createObjectURL(blob));
      toast.success("Closing slip photo saved");
      onUploaded?.(data.photo);
    } catch (err) { toast.error(formatError(err)); }
    finally { setBusy(false); e.target.value = ""; }
  };

  const done = !!(preview || existing);
  return (
    <Card className={`p-5 rounded-2xl border-2 ${done ? "border-emerald-300 bg-emerald-50/40" : "border-[color:var(--bpcl-orange)] bg-amber-50/40"}`} data-testid={`photo-block-${side}`}>
      <div className="flex items-center justify-between gap-2 mb-3">
        <div className="text-sm font-semibold text-slate-700 flex items-center gap-2">
          <Camera className="w-4 h-4 text-[color:var(--bpcl-blue)]" /> {label}
          <span className="text-[10px] uppercase tracking-wide rounded-full px-2 py-0.5 bg-red-100 text-red-700 font-bold">Required</span>
        </div>
        {done && <CheckCircle2 className="w-5 h-5 text-emerald-600" data-testid={`photo-done-${side}`} />}
      </div>
      <input ref={inputRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={pick} data-testid={`photo-input-${side}`} />
      {shown ? (
        <div className="flex gap-3 items-start">
          <img src={shown} alt="Closing slip" className="w-28 h-36 object-cover rounded-xl border border-slate-200 bg-white" data-testid={`photo-preview-${side}`} />
          <div className="flex-1 text-sm text-slate-600">
            <div className="font-medium text-emerald-700">Photo attached</div>
            <div className="text-xs mt-1">It will be printed on the DSM Duty Slip.</div>
            <Button type="button" variant="outline" size="sm" className="rounded-full mt-3" disabled={busy} onClick={() => inputRef.current?.click()} data-testid={`photo-retake-${side}`}>
              <RefreshCw className="w-3.5 h-3.5 mr-1" /> Retake
            </Button>
          </div>
        </div>
      ) : (
        <Button type="button" disabled={busy} onClick={() => inputRef.current?.click()} data-testid={`photo-capture-btn-${side}`}
          className="w-full h-14 rounded-xl bg-[color:var(--bpcl-blue)] hover:bg-[color:var(--bpcl-blue-dark)] text-base font-semibold">
          {busy ? <><Loader2 className="w-5 h-5 mr-2 animate-spin" /> Uploading {progress}%</> : <><Camera className="w-5 h-5 mr-2" /> TAKE PHOTO OF CLOSING SLIP</>}
        </Button>
      )}
      {!done && <div className="text-xs text-slate-500 mt-2">Take a clear photo of the printed closing slip from the MPD before entering readings.</div>}
    </Card>
  );
}
